import {
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import tileApi from 'apis/disApi/tileApi'
import _, { isEmpty, isEqual, uniqBy } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { logErrorMessage } from 'utils/functions/helpers'
import useForceRerender from 'utils/hooks/useForceRerender'
import {
	parsedDataGridRecords,
	getViewClientFilters,
	getSavedFilterList,
} from '../../../../data/dataHelpers'
import {
	cancelLoadNextRecordsAction,
	getViewTabViewRecords,
	loadNextRecordsQuery,
	loadRecordsQuery,
} from '../../../../data/dataServices'
import DoformsDataChart from '../../../../data/datagrid/DoformsDataChart'
import useFields from '../../hooks/useFields'
import useView from '../../hooks/useView'
import DashboardList from '../DashboardKeyList'
import ViewsData from 'components/pages/views/ViewsData'
import OtherOptions from 'components/pages/dis-v2/_components/OtherOptions'
import { useRecordQuery } from '../utils/tileRecordQuery'
import { PopUpColorPicker } from 'custom-components/ColorPicker/ColorPicker'

const ChartForm = ({
	dashboardKey,
	tileKey, // Optional
	viewKeySetting,
	filterData,
	onFilterDataChange,
	enableLoadView,
	dashboardKeyList,
	outlinedInput,
	setIsChartLoading, // Optional
	masterDateTimeConditions,
	dispatchChartData,
	chartSetting: defaultChartSetting,
	setChartSetting: setDefaultChartSetting,
}) => {
	const { environment } = useSelector((state) => state)

	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const { viewLoading, views } = useView({ config: { enabled: enableLoadView } })
	const [forceId, setForceRerender] = useForceRerender()

	function onMapTileInfoChange(id, value) {
		const newMapTileInfo = { ...(filterData?.mapTileInfo || {}), [id]: value }
		onFilterDataChange('mapTileInfo', newMapTileInfo)
	}

	const [currentRecords, setCurrentRecords] = useState(null)

	const [conditionFilters, setConditionFilters] = useState({})

	const OTHER_OPTIONS = [
		//{ label: t('common:chart.showSubmit'), value: 'showSubmit' },
		{
			label: t('common:chart.showResizeSlider'),
			value: 'showResizeSlider',
		},
	]

	const { viewKey } = filterData

	const { fields } = useFields({
		viewKey,
	})

	const [chartSetting, setChartSetting] = useState({})
	const [chartColor1, setChartColor1] = useState('#000000')
	const [chartColor2, setChartColor2] = useState('#000000')
	const [chartColor3, setChartColor3] = useState('#000000')
	const [chartDataTextColor, setChartDataTextColor] = useState('#000000')
	const [customGraphText, setCustomGraphText] = useState('')

	const queryKey = useMemo(() => {
		if (!viewKey) return null
		return ['chart-tile', viewKey]
	}, [viewKey])

	useEffect(() => {
		if (!queryKey || !queryClient) return
		if (isEmpty(filterData?.chart) && queryKey) {
			queryClient.invalidateQueries(queryKey)
		}
	}, [filterData?.chart, queryKey, queryClient])

	const { data, isLoading, isFetching } = useQuery({
		queryKey,
		queryFn: async () => {
			// const [tileResponse, viewResponse] = await Promise.all([
			// 	tileApi.get(dashboardKey, tileKey, environment.apiToken),

			// ])

			const viewResponse = await getViewTabViewRecords({
				viewKey: filterData?.viewKey,
				viewSession: false,
				token: environment.apiToken,
			})

			// let chart

			// if (tileResponse?.data?.chart) {
			// 	chart = tileResponse?.data?.chart
			// } else if (viewResponse?.data?.chart) {
			// 	chart = viewResponse?.data?.chart
			// }

			const conditions = viewResponse.data.queries[0]?.filter?.conditions ?? []
			const clientFilter = viewResponse?.data?.clientFilter
				? JSON.parse(viewResponse?.data?.clientFilter)
				: []

			const savedFilterList = getSavedFilterList(clientFilter)
			const currentFiltersWithUserFilter = getViewClientFilters(
				savedFilterList,
				environment?.userCurrent?.dataGroupKey
			)

			const viewFilter = {
				items: currentFiltersWithUserFilter?.items
					? currentFiltersWithUserFilter.items
					: currentFiltersWithUserFilter,
				conditions,
			}

			return {
				viewChart: viewResponse?.data?.chart || {},
				columns: viewResponse?.data?.columns || [],
				viewFilter,
			}
		},
		enabled: !!queryKey,
		staleTime: Infinity,
	})

	const masterDateTimeOperator = useMemo(() => {
		if (!isEmpty(masterDateTimeConditions)) {
			const allConditions = []
			for (const key in masterDateTimeConditions) {
				const value = masterDateTimeConditions[key]
				const condition = {
					...value,
					target: key,
					join: 'AND',
				}
				allConditions.push(condition)
			}
			return allConditions
		}
		return []
	}, [masterDateTimeConditions])

	useEffect(() => {
		if (
			filterData?.otherOptionsChecked === undefined ||
			filterData?.otherOptionsChecked?.includes('isNewShowResizeSlider') === false
		) {
			onFilterDataChange('otherOptionsChecked', ['showResizeSlider', 'isNewShowResizeSlider'])
		}
	}, [filterData])

	// get view records by view filter
	useEffect(() => {
		if (isLoading || isFetching || isEmpty(data)) return

		// only init viewChart when tileChart is empty
		if (isEmpty(filterData?.chart)) {
			// set default setting for Chart TileTile
			if (setDefaultChartSetting) {
				if (isEqual(data?.viewChart, chartSetting)) return
				setDefaultChartSetting(data?.viewChart)
			}
		}
	}, [data, isLoading, isFetching])

	useEffect(() => {
		if (isEqual(chartSetting, filterData?.chart)) return
		setChartSetting(filterData?.chart)
	}, [filterData?.chart])

	const { records, recordsLoading } = useRecordQuery({
		viewKey: filterData?.viewKey,
		clientFilter: conditionFilters,
	})

	useEffect(() => {
		// should fetch record
		setIsChartLoading?.(recordsLoading)
		if (recordsLoading) return

		if (isEqual(records, currentRecords)) return
		setCurrentRecords(records)
	}, [records, recordsLoading])

	// get view records by masterDateTimeOperator
	useEffect(() => {
		;(async () => {
			if (isEmpty(data?.viewFilter) || isEmpty(masterDateTimeOperator)) {
				setConditionFilters(data?.viewFilter || {})
				return
			}

			setIsChartLoading?.(true)
			const clonedFilter = _.cloneDeep(data?.viewFilter)
			const checkedConditions = clonedFilter.conditions.map((condition, index) => {
				const keyToCheck = index === 0 ? 'Master_DateTime' : index === 1 ? 'Master_Text' : ''
				if (!keyToCheck) return condition

				const newCondition = masterDateTimeOperator.find(
					(operator) => operator.keyData === keyToCheck
				)
				if (!newCondition) return condition

				return newCondition
			})

			clonedFilter.conditions = checkedConditions
			const shouldNotUpdate = _.isEqual(clonedFilter, conditionFilters)
			if (shouldNotUpdate) {
				setIsChartLoading?.(false)
				return
			}

			setConditionFilters(clonedFilter)
			setIsChartLoading?.(false)
		})()
	}, [masterDateTimeOperator, data?.viewFilter])

	const gridRows = useMemo(() => {
		if (!currentRecords?.length || !data?.columns?.length) return []

		return parsedDataGridRecords(currentRecords, data?.columns, environment, [], [], false)
	}, [currentRecords, JSON.stringify(data?.columns)])

	const displayGridRows = useMemo(() => {
		if (!currentRecords?.length || !data?.columns?.length) return []

		return parsedDataGridRecords(currentRecords, data?.columns, environment, [], [], true)
	}, [currentRecords, JSON.stringify(data?.columns)])

	const addChartLoading = isLoading && isFetching
	useEffect(() => {
		if (isEqual(filterData?.viewKey, viewKeySetting)) return
		if (viewKeySetting) {
			onFilterDataChange('viewKey', viewKeySetting)
		}
	}, [viewKeySetting])

	useEffect(() => {
		if (!dispatchChartData) return
		dispatchChartData({
			type: 'columns',
			payload: data?.columns,
		})
	}, [JSON.stringify(data?.columns)])

	useEffect(() => {
		if (!dispatchChartData) return
		dispatchChartData({
			type: 'gridRows',
			payload: gridRows,
		})
	}, [JSON.stringify(gridRows)])

	useEffect(() => {
		if (isEmpty(chartSetting)) return

		if (isEqual(chartSetting, filterData?.chart)) return
		onFilterDataChange('chart', chartSetting)
	}, [JSON.stringify(chartSetting)])

	const handleSelectViewKey = (newViewKey) => {
		const selectedView = views.find((view) => view.key === newViewKey)

		onFilterDataChange('viewKey', newViewKey)

		if (selectedView) {
			onFilterDataChange('formKey', selectedView?.formKey)
			onFilterDataChange('projectKey', selectedView?.projectKey)
		}
	}

	const handleLinkedChange = (id, value) => {
		if (value === '') {
			// Remove the field from linked
			const newLinkedFields = { ...filterData?.mapTileInfo?.linkedFields }
			delete newLinkedFields[id]
			onMapTileInfoChange('linkedFields', newLinkedFields)
		} else {
			const newLinked = { ...filterData?.mapTileInfo?.linkedFields, [id]: value }
			onMapTileInfoChange('linkedFields', newLinked)
		}
	}

	const formattedOtherOptions = OTHER_OPTIONS.map((item) => {
		if (item.value !== 'showResizeSlider') {
			return { ...item, hide: false }
		}
		return item
	})

	const handleChangeValue = (value) => {
		onFilterDataChange('otherOptionsChecked', value)
	}
	return (
		<>
			{!viewLoading ? (
				<FormControl
					variant={outlinedInput ? 'outlined' : 'standard'}
					sx={{ m: outlinedInput ? 0 : 1, minWidth: outlinedInput ? '50%' : 120 }}
					size="small"
				>
					<InputLabel id="view-select-small-label">{t('common:input.view')}</InputLabel>
					<Select
						labelId="view-select-small-label"
						id="view-select-small"
						value={viewKey || ''}
						label={t('common:input.view')}
						onChange={(e) => handleSelectViewKey(e.target.value)}
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{views?.length > 0 &&
							views
								.sort((a, b) => a.name.localeCompare(b.name))
								.map((viewItem) => (
									<MenuItem value={viewItem.key} key={viewItem.key}>
										{viewItem.name}
									</MenuItem>
								))}
					</Select>
				</FormControl>
			) : (
				<Skeleton
					variant="rectangular"
					height={45}
					sx={{ mt: outlinedInput ? 0 : 2, width: outlinedInput ? '40%' : 120 }}
				/>
			)}
			<Box sx={{ py: 2 }}>
				{addChartLoading && <Skeleton height={40} width={105} sx={{ my: 2 }} />}
				{!!data && (
					<DoformsDataChart
						hiddenDisplayChartBtn
						isAddChart={!chartSetting?.display}
						showOnlyChart={false}
						columns={data?.columns || []}
						gridRows={displayGridRows || []}
						chartSetting={chartSetting || {}}
						setChartSetting={(newChartData) => {
							setChartSetting(newChartData)
						}}
						recordsLoading={false}
						environment={environment}
						onForceUpdate={setForceRerender}
						showResizeSlider={
							filterData?.otherOptionsChecked?.includes('showResizeSlider') ? true : false
						}
					/>
				)}
			</Box>

			<Box>
				<DashboardList
					title={t('common:dis.dashboardKeys')}
					list={dashboardKeyList}
					fields={fields}
					onFieldsChange={handleLinkedChange}
					linkedFields={filterData?.mapTileInfo?.linkedFields || {}}
					getLabelOption={(item) => item.name}
				/>
			</Box>

			<Divider sx={{ mt: '5px' }} />
			<Typography variant="subtitle2" sx={{ mb: '5px' }}>
				{t('common:misc.customChartOptions')}
			</Typography>
			<Typography variant="body2">{t('misc.chartColor1')}</Typography>
			<PopUpColorPicker
				value={filterData?.chartColor1 || '#000000'}
				onColorChange={(color) => onFilterDataChange('chartColor1', color)}
				customStyle={{
					label: {
						display: 'flex',
						alignItems: 'center',
						height: '39px',
					},
					circle: {
						width: '20px',
						height: '20px',
					},
				}}
			/>
			<Typography variant="body2" sx={{ mt: '5px' }}>
				{t('common:misc.chartColor2')}
			</Typography>
			<PopUpColorPicker
				value={filterData?.chartColor2 || '#000000'}
				onColorChange={(color) => onFilterDataChange('chartColor2', color)}
				customStyle={{
					label: {
						display: 'flex',
						alignItems: 'center',
						height: '39px',
					},
					circle: {
						width: '20px',
						height: '20px',
					},
				}}
			/>
			<Typography variant="body2" sx={{ mt: '5px' }}>
				{t('common:misc.chartColor3')}
			</Typography>
			<PopUpColorPicker
				value={filterData?.chartColor3 || '#000000'}
				onColorChange={(color) => onFilterDataChange('chartColor3', color)}
				customStyle={{
					label: {
						display: 'flex',
						alignItems: 'center',
						height: '39px',
					},
					circle: {
						width: '20px',
						height: '20px',
					},
				}}
			/>
			<Typography variant="body2" sx={{ mt: '5px' }}>
				{t('common:misc.chartDataTextColor')}
			</Typography>
			<PopUpColorPicker
				value={filterData?.chartDataTextColor || '#000000'}
				onColorChange={(color) => onFilterDataChange('chartDataTextColor', color)}
				customStyle={{
					label: {
						display: 'flex',
						alignItems: 'center',
						height: '39px',
					},
					circle: {
						width: '20px',
						height: '20px',
					},
				}}
			/>
			<TextField
				fullWidth
				size="small"
				label={t('common:misc.customGraphText')}
				value={filterData.customGraphText || ''}
				onChange={(e) => onFilterDataChange('customGraphText', e.target.value)}
				sx={{ mt: '10px', mb: '20px' }}
			/>

			<Box spacing={2} sx={{ width: '100% !important' }}>
				<OtherOptions
					optionList={formattedOtherOptions}
					checkedOptions={filterData?.otherOptionsChecked}
					onChangeOptions={handleChangeValue}
					// onChangeOptions={(value) => {
					// 	let a = tilePositionList
					// 	onFilterDataChange('otherOptionsChecked', value)
					// }}
				/>
			</Box>
		</>
	)
}

export default ChartForm
